/**
 * SurveyJS stores a question comment beside the answer, under
 * `valueName + commentSuffix` (default `-Comment`). Setting Other to
 * `{ other: otherText }` copies that same object onto both keys.
 *
 * An echoed companion is dropped only when it repeats its own base
 * question. A comment whose text differs from that question is kept, even
 * when some other answer happens to hold the same text. The suffix comes
 * from survey-core settings, not from the answer shape.
 *
 * One-shot pure function. Not an LLM prompt.
 */

import { settings } from 'survey-core';

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!isRecord(value)) return value;
  const sorted: Record<string, unknown> = {};
  for (const key of Object.keys(value).sort()) {
    sorted[key] = canonicalize(value[key]);
  }
  return sorted;
}

function sameValue(left: unknown, right: unknown): boolean {
  return JSON.stringify(canonicalize(left)) === JSON.stringify(canonicalize(right));
}

function isCommentCompanionKey(key: string, suffix: string): boolean {
  return suffix.length > 0 && key.length > suffix.length && key.endsWith(suffix);
}

function hasOwn(record: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

/**
 * Name-keyed maps match `baseName` itself. Title-keyed maps pass
 * `baseKeyFor`; that result is the only owner, even when some other
 * question's title equals `baseName`. An unresolved title keeps the comment.
 */
function baseKeyIn(
  answers: Record<string, unknown>,
  baseName: string,
  baseKeyFor?: (baseName: string) => string | undefined,
): string | undefined {
  if (baseKeyFor) {
    const mapped = baseKeyFor(baseName);
    if (typeof mapped !== 'string' || !mapped || !hasOwn(answers, mapped)) return undefined;
    return mapped;
  }
  if (hasOwn(answers, baseName)) return baseName;
  return undefined;
}

function omitRecord(
  answers: Record<string, unknown>,
  suffix: string,
  baseKeyFor?: (baseName: string) => string | undefined,
): Record<string, unknown> {
  const keys = Object.keys(answers);
  const drop = new Set<string>();
  for (const key of keys) {
    if (!isCommentCompanionKey(key, suffix)) continue;
    const baseName = key.slice(0, -suffix.length);
    const ownerKey = baseKeyIn(answers, baseName, baseKeyFor);
    if (!ownerKey) continue;
    if (sameValue(answers[ownerKey], answers[key])) drop.add(key);
  }
  const kept: Record<string, unknown> = {};
  for (const key of keys) {
    if (drop.has(key)) continue;
    kept[key] = omitValue(answers[key], suffix);
  }
  return kept;
}

function omitValue(value: unknown, suffix: string): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => omitValue(item, suffix));
  }
  if (!isRecord(value)) return value;
  return omitRecord(value, suffix);
}

export interface SurveyCommentCompanionOptions {
  /** Defaults to the active survey-core `commentSuffix`. */
  commentSuffix?: string;
  /**
   * Question name → key stored in `answers`. Title-keyed maps need this.
   * When it does not resolve, the comment is kept.
   * Nested rows ignore it and match column names in that row.
   */
  baseKeyFor?: (baseName: string) => string | undefined;
}

/**
 * Remove SurveyJS comment-channel keys that repeat their own question
 * (including inside matrix rows). A comment is not compared with other
 * answers.
 */
export function omitEchoedSurveyCommentCompanions(
  answers: Record<string, unknown>,
  options?: SurveyCommentCompanionOptions,
): Record<string, unknown> {
  const suffix = options?.commentSuffix ?? settings.commentSuffix;
  if (typeof suffix !== 'string' || suffix.length === 0) return answers;
  return omitRecord(answers, suffix, options?.baseKeyFor);
}
